<template>
  <div v-if="step" class="gt" :class="{ 'gt--mobile': mobile }" role="dialog" aria-modal="true" :aria-label="title">
    <!-- Sin elemento: fondo entero. Con elemento: cuatro franjas alrededor, así el hueco
         queda libre para hacer click cuando el paso lo pide. -->
    <template v-if="rect">
      <div class="gt-shade" :style="shade.top"></div>
      <div class="gt-shade" :style="shade.bottom"></div>
      <div class="gt-shade" :style="shade.left"></div>
      <div class="gt-shade" :style="shade.right"></div>
      <div class="gt-ring" :class="{ 'is-clickable': step.advanceOn === 'click' || step.interactive }" :style="holeStyle"></div>
      <div v-if="step.advanceOn !== 'click' && !step.interactive" class="gt-block" :style="holeStyle"></div>
    </template>
    <div v-else class="gt-shade gt-shade--full"></div>

    <div ref="card" class="gt-card" :class="`gt-card--${cardPlacement}`" :style="cardStyle" @click.stop>
      <div class="gt-card__top">
        <span class="gt-card__count">{{ $tn("tour.step", { n: index + 1, total: steps.length }) }}</span>
        <button type="button" class="gt-card__close" :aria-label="$tn('tour.skip')" @click="close">
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>
      <h3 v-if="title" class="gt-card__title">{{ title }}</h3>
      <p class="gt-card__body">{{ body }}</p>
      <p v-if="step.advanceOn === 'click'" class="gt-card__hint">
        <i class="fas fa-hand-pointer" aria-hidden="true"></i> {{ $tn("tour.clickToContinue") }}
      </p>
      <div class="gt-card__dots" aria-hidden="true">
        <span v-for="(s, i) in steps" :key="i" class="gt-dot" :class="{ 'is-on': i === index }"></span>
      </div>
      <div class="gt-card__actions">
        <button type="button" class="gt-btn gt-btn--link" @click="close">{{ $tn("tour.skip") }}</button>
        <span class="gt-spacer"></span>
        <button v-if="index > 0" type="button" class="gt-btn" @click="prev">{{ $tn("tour.back") }}</button>
        <button v-if="step.advanceOn !== 'click'" type="button" class="gt-btn gt-btn--primary" @click="next">
          {{ isLast ? $tn("tour.done") : $tn("tour.next") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Muestra el paso actual de un recorrido (tourService): oscurece la pantalla, deja a la
 * vista el elemento del paso y pone al lado una tarjeta con el texto. Sigue al elemento
 * si se mueve o se scrollea. Teclado: Esc cierra, ← / → navegan.
 */
import { tourState, goToStep, endTour } from "./tourService";

const PAD = 6;
const GAP = 12;
const MARGIN = 12;
const CARD_W = 330;

export default {
  name: "GuidedTour",
  data() {
    return { rect: null, cardSize: { w: CARD_W, h: 180 }, raf: null, clickTarget: null, missingSince: 0 };
  },
  computed: {
    tour() {
      return tourState.tour;
    },
    steps() {
      return (this.tour && this.tour.steps) || [];
    },
    index() {
      return tourState.index;
    },
    step() {
      return this.steps[this.index] || null;
    },
    mobile() {
      return !!(this.tour && this.tour.mobile);
    },
    isLast() {
      return this.index === this.steps.length - 1;
    },
    title() {
      return this.step && this.step.titleKey ? this.$tn(this.step.titleKey) : "";
    },
    body() {
      if (!this.step) return "";
      const key = this.mobile && this.step.bodyMobileKey ? this.step.bodyMobileKey : this.step.bodyKey;
      return key ? this.$tn(key) : "";
    },
    hole() {
      const r = this.rect;
      if (!r) return null;
      return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
    },
    holeStyle() {
      const h = this.hole;
      return h ? { top: `${h.top}px`, left: `${h.left}px`, width: `${h.width}px`, height: `${h.height}px` } : {};
    },
    shade() {
      const h = this.hole;
      if (!h) return {};
      const right = h.left + h.width;
      const bottom = h.top + h.height;
      return {
        top: { top: 0, left: 0, right: 0, height: `${Math.max(0, h.top)}px` },
        bottom: { top: `${bottom}px`, left: 0, right: 0, bottom: 0 },
        left: { top: `${h.top}px`, left: 0, width: `${Math.max(0, h.left)}px`, height: `${h.height}px` },
        right: { top: `${h.top}px`, left: `${right}px`, right: 0, height: `${h.height}px` },
      };
    },
    /** Dónde va la tarjeta: la preferencia del paso si entra; si no, donde haya lugar. */
    cardPlacement() {
      const h = this.hole;
      if (!h || this.mobile) return h ? "dock" : "center";
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const { w, h: ch } = this.cardSize;
      const fits = {
        bottom: h.top + h.height + GAP + ch <= vh - MARGIN,
        top: h.top - GAP - ch >= MARGIN,
        right: h.left + h.width + GAP + w <= vw - MARGIN,
        left: h.left - GAP - w >= MARGIN,
      };
      const order = [this.step.placement, "bottom", "top", "right", "left"].filter(Boolean);
      return order.find((p) => fits[p]) || "inside";
    },
    cardStyle() {
      const h = this.hole;
      const p = this.cardPlacement;
      if (p === "center" || p === "dock") return {};
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const { w, h: ch } = this.cardSize;
      const clampX = (x) => Math.min(Math.max(x, MARGIN), vw - w - MARGIN);
      const clampY = (y) => Math.min(Math.max(y, MARGIN), vh - ch - MARGIN);
      let left;
      let top;
      if (p === "bottom") {
        top = h.top + h.height + GAP;
        left = clampX(h.left + h.width / 2 - w / 2);
      } else if (p === "top") {
        top = h.top - GAP - ch;
        left = clampX(h.left + h.width / 2 - w / 2);
      } else if (p === "right") {
        left = h.left + h.width + GAP;
        top = clampY(h.top + h.height / 2 - ch / 2);
      } else if (p === "left") {
        left = h.left - GAP - w;
        top = clampY(h.top + h.height / 2 - ch / 2);
      } else {
        // El elemento ocupa casi toda la pantalla: la tarjeta va adentro, abajo.
        left = clampX(h.left + h.width - w - MARGIN);
        top = clampY(h.top + h.height - ch - MARGIN);
      }
      return { left: `${left}px`, top: `${top}px` };
    },
  },
  watch: {
    step: {
      handler(step, old) {
        this.detachClick();
        // Mientras corre un recorrido, <body> lo sabe (p. ej. para mostrar controles que
        // solo aparecen al pasar el mouse).
        document.body.classList.toggle("gt-running", !!step);
        if (!step) {
          this.stopTracking();
          document.removeEventListener("keydown", this.onKey, true);
          return;
        }
        if (!old) document.addEventListener("keydown", this.onKey, true);
        this.rect = null;
        this.missingSince = Date.now();
        this.scrolled = false;
        this.startTracking();
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    document.body.classList.remove("gt-running");
    this.stopTracking();
    this.detachClick();
    document.removeEventListener("keydown", this.onKey, true);
  },
  methods: {
    findTarget() {
      const t = this.step && this.step.target;
      if (!t) return null;
      try {
        const el = typeof t === "function" ? t() : document.querySelector(t);
        if (!el || !el.isConnected) return null;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return null;
        return el;
      } catch (e) {
        return null;
      }
    },
    /** Sigue al elemento en cada frame (se mueve con el scroll, las animaciones, etc.). */
    startTracking() {
      this.stopTracking();
      const tick = () => {
        const el = this.findTarget();
        if (el) {
          if (!this.scrolled) {
            this.scrolled = true;
            el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
          }
          const r = el.getBoundingClientRect();
          const cur = this.rect;
          if (!cur || cur.top !== r.top || cur.left !== r.left || cur.width !== r.width || cur.height !== r.height) {
            this.rect = { top: r.top, left: r.left, width: r.width, height: r.height };
          }
          if (this.step.advanceOn === "click" && this.clickTarget !== el) this.attachClick(el);
        } else if (this.rect && Date.now() - this.missingSince > 400) {
          this.rect = null;
        }
        const card = this.$refs.card;
        if (card) {
          const w = card.offsetWidth;
          const h = card.offsetHeight;
          if (w !== this.cardSize.w || h !== this.cardSize.h) this.cardSize = { w, h };
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    },
    stopTracking() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    },
    attachClick(el) {
      this.detachClick();
      this.clickTarget = el;
      this.onTargetClick = () => {
        // Se deja actuar al click (p. ej. abrir un popup) antes de pasar al paso siguiente.
        setTimeout(() => this.next(), 350);
      };
      el.addEventListener("click", this.onTargetClick, true);
    },
    detachClick() {
      if (this.clickTarget && this.onTargetClick) {
        this.clickTarget.removeEventListener("click", this.onTargetClick, true);
      }
      this.clickTarget = null;
      this.onTargetClick = null;
    },
    next() {
      goToStep(this.index + 1);
    },
    prev() {
      goToStep(this.index - 1);
    },
    close() {
      endTour("close");
    },
    onKey(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        this.close();
      } else if (e.key === "ArrowRight" && this.step.advanceOn !== "click") {
        this.next();
      } else if (e.key === "ArrowLeft" && this.index > 0) {
        this.prev();
      }
    },
  },
};
</script>

<style>
/* Tokens propios: el recorrido se monta en <body>, fuera de cualquier pantalla. */
:root {
  --gt-surface: var(--bs-body-bg);
  --gt-text: var(--bs-body-color);
  --gt-muted: var(--bs-secondary-color);
  --gt-border: var(--bs-border-color);
  --gt-shade: rgba(var(--bs-black-rgb), 0.55);
  --gt-shadow: var(--bs-box-shadow-lg);
}
html#darkdesign {
  --gt-surface: var(--dark-bg-card);
  --gt-text: var(--dark-text-primary);
  --gt-muted: var(--dark-text-secondary);
  --gt-border: var(--dark-border-color);
  --gt-shade: rgba(var(--bs-black-rgb), 0.7);
  --gt-shadow: var(--dark-shadow-lg);
}
</style>

<style scoped>
.gt { position: fixed; inset: 0; z-index: 1090; pointer-events: none; }
.gt-shade { position: fixed; background: var(--gt-shade); pointer-events: auto; transition: all 0.18s ease; }
.gt-shade--full { inset: 0; }
.gt-ring {
  position: fixed;
  border-radius: 10px;
  box-shadow: 0 0 0 2px var(--bs-primary), 0 0 0 6px rgba(var(--bs-primary-rgb), 0.25);
  pointer-events: none;
  transition: all 0.18s ease;
}
.gt-ring.is-clickable { animation: gt-pulse 1.4s ease-in-out infinite; }
@keyframes gt-pulse {
  0%, 100% { box-shadow: 0 0 0 2px var(--bs-primary), 0 0 0 6px rgba(var(--bs-primary-rgb), 0.25); }
  50% { box-shadow: 0 0 0 2px var(--bs-primary), 0 0 0 12px rgba(var(--bs-primary-rgb), 0.12); }
}
/* Paso sin interacción: el elemento se ve pero no se toca (no se arrastra sin querer). */
.gt-block { position: fixed; pointer-events: auto; }
.gt-card {
  position: fixed;
  width: 330px;
  max-width: calc(100vw - 24px);
  pointer-events: auto;
  background: var(--gt-surface);
  color: var(--gt-text);
  border: 1px solid var(--gt-border);
  border-radius: 14px;
  box-shadow: var(--gt-shadow);
  padding: 14px 16px 12px;
  transition: top 0.18s ease, left 0.18s ease;
}
.gt-card--center { top: 50%; left: 50%; transform: translate(-50%, -50%); width: 380px; }
.gt-card--dock {
  left: 12px;
  right: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom));
  width: auto;
  max-width: none;
}
.gt--mobile .gt-card--center { width: calc(100vw - 24px); }
.gt-card__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.gt-card__count { font-size: 0.72rem; font-weight: 600; color: var(--bs-primary); letter-spacing: 0.03em; }
.gt-card__close { border: 0; background: transparent; color: var(--gt-muted); width: 28px; height: 28px; border-radius: 50%; cursor: pointer; }
.gt-card__close:hover { color: var(--gt-text); }
.gt-card__title { margin: 0 0 6px; font-size: 1rem; font-weight: 700; }
.gt-card__body { margin: 0; font-size: 0.88rem; line-height: 1.45; color: var(--gt-text); }
.gt-card__hint { margin: 8px 0 0; font-size: 0.78rem; color: var(--bs-primary); font-weight: 600; }
.gt-card__dots { display: flex; gap: 4px; margin: 12px 0 10px; }
.gt-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--gt-border); }
.gt-dot.is-on { background: var(--bs-primary); width: 16px; border-radius: 3px; }
.gt-card__actions { display: flex; align-items: center; gap: 6px; }
.gt-spacer { flex: 1; }
.gt-btn {
  border: 1px solid var(--gt-border);
  background: transparent;
  color: var(--gt-text);
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 0.82rem;
  cursor: pointer;
}
.gt-btn--primary { background: var(--bs-primary); border-color: var(--bs-primary); color: var(--bs-white); }
.gt-btn--link { border: 0; padding: 5px 4px; color: var(--gt-muted); }
.gt-btn--link:hover { color: var(--gt-text); }
</style>
